import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Send, MessageCircle, AlertTriangle } from "lucide-react";

/**
 * Shared threaded messages inbox for patient + doctor.
 * Props:
 *  - role: "patient" | "doctor"
 */
export default function Inbox({ role }) {
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [thread, setThread] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [partners, setPartners] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/messages/threads");
    setThreads(data);
  };

  useEffect(() => { load(); api.get("/messages/partners").then(r => setPartners(r.data)); }, []);

  useEffect(() => {
    if (!selected) return;
    api.get(`/messages/thread/${selected.id}`).then(r => setThread(r.data));
  }, [selected]);

  useEffect(() => {
    scrollRef.current?.scrollTo(0, 9999999);
  }, [thread]);

  const send = async () => {
    if (!input.trim() || sending || !selected) return;
    setSending(true);
    try {
      await api.post("/messages", { to_user_id: selected.id, content: input.trim() });
      setInput("");
      const { data } = await api.get(`/messages/thread/${selected.id}`);
      setThread(data);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send");
    } finally { setSending(false); }
  };

  const startWith = (p) => {
    setSelected(p);
    setShowPicker(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="cm-eyebrow mb-2">Messages</div>
          <h1 className="cm-h2">{role === "doctor" ? "Patient messages" : "Talk to your doctors"}</h1>
        </div>
      </div>
      <div className="cm-card border-[#d97706]/30 bg-[#fffbeb]" data-testid="msg-safety-notice">
        <div className="flex items-start gap-2 text-sm text-[#92400e]">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0"/>
          <div><b>This messaging service is not an emergency service.</b> For urgent concerns, book a consultation or call 999.</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 min-h-[560px]">
        <div className="cm-card p-0 overflow-hidden lg:col-span-1">
          <div className="p-4 border-b border-[#e2e8f0] flex items-center justify-between">
            <div className="font-semibold text-[#1a2b4a] text-sm">Conversations</div>
            <button className="text-xs font-semibold text-[#4a90d9]" onClick={()=>setShowPicker(v => !v)} data-testid="msg-new-btn">
              {showPicker ? "Close" : "New"}
            </button>
          </div>
          {showPicker && (
            <div className="p-3 border-b border-[#e2e8f0] bg-[#f5f7fa]" data-testid="msg-picker">
              <div className="text-xs text-[#475569] mb-2">Start a conversation with:</div>
              {partners.length === 0 && <div className="text-xs text-[#475569]">You'll be able to message {role==="doctor"?"patients":"doctors"} after your first appointment.</div>}
              <div className="space-y-1">
                {partners.map(p => (
                  <button key={p.id} onClick={()=>startWith(p)} className="w-full text-left rounded-md p-2 hover:bg-white text-sm" data-testid={`msg-partner-${p.id}`}>
                    <div className="font-medium text-[#1a2b4a]">{p.name}</div>
                    <div className="text-xs text-[#94a3b8] capitalize">{p.role}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="divide-y divide-[#e2e8f0] max-h-[520px] overflow-y-auto" data-testid="msg-threads">
            {threads.length === 0 && !showPicker && (
              <div className="p-4 text-sm text-[#475569] flex flex-col items-center gap-2 text-center">
                <MessageCircle size={24} className="text-[#94a3b8]"/>
                No conversations yet. Once you have an appointment, you can message here.
              </div>
            )}
            {threads.map(t => (
              <button key={t.partner.id} onClick={()=>setSelected(t.partner)}
                className={`w-full text-left p-3 flex items-start gap-3 ${selected?.id === t.partner.id ? "bg-[#ebf3fc]" : "hover:bg-[#f5f7fa]"}`}
                data-testid={`msg-thread-${t.partner.id}`}>
                <div className="w-9 h-9 rounded-full bg-[#ebf3fc] text-[#1a2b4a] font-semibold flex items-center justify-center flex-shrink-0">
                  {(t.partner.name || "?").split(" ").slice(-2).map(s => s[0]).join("")}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-sm text-[#1a2b4a] truncate">{t.partner.name}</div>
                    {t.unread > 0 && <span className="text-[10px] font-bold bg-[#4a90d9] text-white rounded-full px-1.5 min-w-[18px] text-center">{t.unread}</span>}
                  </div>
                  <div className="text-xs text-[#475569] truncate">{t.last_message?.content || "No messages yet"}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="cm-card p-0 flex flex-col lg:col-span-2 min-h-[560px]">
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-sm text-[#475569] p-8 text-center">
              Select a conversation on the left or start a new one.
            </div>
          ) : (
            <>
              <div className="p-4 border-b border-[#e2e8f0] flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#ebf3fc] text-[#1a2b4a] font-semibold flex items-center justify-center flex-shrink-0">
                  {(selected.name || "?").split(" ").slice(-2).map(s => s[0]).join("")}
                </div>
                <div>
                  <div className="font-semibold text-[#1a2b4a]" data-testid="msg-partner-name">{selected.name}</div>
                  <div className="text-xs text-[#94a3b8] capitalize">{selected.role}</div>
                </div>
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="msg-messages">
                {thread?.messages?.length === 0 && <div className="text-sm text-[#475569] text-center py-8">Say hello.</div>}
                {thread?.messages?.map((m,i) => {
                  const mine = m.from_user_id !== selected.id;
                  return (
                    <div key={m.id || i} className={mine ? "flex justify-end" : "flex justify-start"}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${mine ? "bg-[#1a2b4a] text-white rounded-br-sm" : "bg-[#f5f7fa] text-[#0f172a] rounded-bl-sm"}`}>
                        <div>{m.content}</div>
                        <div className={`text-[10px] mt-1 ${mine ? "text-white/60" : "text-[#94a3b8]"}`}>{(m.created_at||"").slice(11,16)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="border-t border-[#e2e8f0] p-3 flex gap-2">
                <input className="cm-input" placeholder="Type a message…" value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e => e.key==="Enter" && send()} disabled={sending} data-testid="msg-input"/>
                <button className="cm-btn-primary" onClick={send} disabled={sending || !input.trim()} data-testid="msg-send"><Send size={16}/></button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
